import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import ScheduleAvailabilityLegend from './schedule-availability-legend'
import CalendarEventBlock from './calendar-event-block'
import {
  blockedSegmentToBooking,
  dedupeBlockedBookings,
  mergeBlockedSegmentsForDisplay,
} from './schedule-availability-layers'
import CalendarBookSlotIcon from './calendar-book-slot-icon'
import {
  getScheduleEventType,
  SCHEDULE_EVENT_TYPES,
} from 'pages/pga-coach/dashboard/todays-schedule/schedule-event-colors'
import {
  DEFAULT_GRID_SCROLL_HOUR,
  formatHourLabel,
  getBookingDateTime,
  getBookingEventKey,
  getDayLabel,
  getEventGridPosition,
  HOUR_HEIGHT,
  HOUR_HEIGHT_MOBILE,
  isHourSlotOccupied,
  isPastBookSlot,
  isToday,
} from './calendar-utils'
import { isHourOutsideBookable } from './schedule-availability-intervals'
import useCalendarStyles from './calendar-styles'

const CalendarTimeGrid = ({
  days,
  bookings,
  startHour,
  endHour,
  title,
  canBookStudent,
  bookingUrl,
  onBookSlotClick,
  onReschedule,
  onPayment,
  onCancellation,
  availabilityByDate,
  showAvailabilityLegend = false,
}) => {
  const classes = useCalendarStyles()
  const theme = useTheme()
  const bodyRef = useRef(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const hourHeight = isMobile ? HOUR_HEIGHT_MOBILE : HOUR_HEIGHT
  const hours = Array.from(
    { length: endHour - startHour },
    (_, index) => startHour + index,
  )
  const isWeekView = days.length > 1
  const labelWidth = isMobile ? 44 : 60
  const gridHeight = hours.length * hourHeight

  const bookingsByDay = days.reduce((acc, day) => {
    const dateKey = day.toISODate()
    acc[dateKey] = bookings.filter(booking =>
      getBookingDateTime(booking).hasSame(day, 'day'),
    )
    return acc
  }, {})

  useEffect(() => {
    const node = bodyRef.current
    if (!node) {
      return
    }

    const earliestBookingHour = bookings.reduce((earliest, booking) => {
      const start = getBookingDateTime(booking)
      if (!start.isValid) {
        return earliest
      }
      return Math.min(earliest, start.hour + start.minute / 60)
    }, DEFAULT_GRID_SCROLL_HOUR)

    // Prefer ~8am, but keep overnight / early-morning events in view.
    const preferredHour = Math.min(
      DEFAULT_GRID_SCROLL_HOUR,
      earliestBookingHour,
    )
    const scrollHour = Math.min(
      Math.max(preferredHour, startHour),
      Math.max(startHour, endHour - 1),
    )
    node.scrollTop = Math.max(0, (scrollHour - startHour) * hourHeight - 40)
  }, [bookings, days, endHour, hourHeight, startHour])

  const renderHourCell = (day, hour, dayBookings, availabilitySegments) => {
    const occupied = isHourSlotOccupied(dayBookings, day, hour)
    const isBookable =
      canBookStudent && bookingUrl && !occupied && !isPastBookSlot(day, hour)
    const outsideBookable =
      availabilitySegments &&
      isHourOutsideBookable(
        hour,
        availabilitySegments.recurring,
        availabilitySegments.override,
      )
    const hourCellClassName = [
      classes.hourCell,
      outsideBookable ? classes.hourCellOutsideBookable : '',
      isBookable ? classes.hourCellBookable : '',
    ]
      .filter(Boolean)
      .join(' ')

    if (!isBookable) {
      return (
        <Box
          key={hour}
          className={hourCellClassName}
          style={{ height: hourHeight }}
          data-testid={
            outsideBookable ? 'calendar-hour-outside-bookable' : undefined
          }
        />
      )
    }

    return (
      <Box
        key={hour}
        className={hourCellClassName}
        style={{ height: hourHeight }}
      >
        <Box
          component="button"
          type="button"
          className={classes.bookSlotTarget}
          onClick={event => onBookSlotClick?.(event, { day, hour })}
          aria-label={`Add to calendar at ${formatHourLabel(
            hour,
          )} on ${day.toFormat('cccc, LLL d')}`}
        >
          <CalendarBookSlotIcon className={classes.bookSlotIcon} />
        </Box>
      </Box>
    )
  }

  return (
    <>
      {title && !isWeekView ? (
        <Box className={classes.calendarHeader}>
          <Typography className={classes.calendarTitle}>{title}</Typography>
        </Box>
      ) : null}

      {isWeekView ? (
        <Box className={classes.weekHeaderRow}>
          <Box
            className={classes.weekHeaderSpacer}
            style={{ width: labelWidth }}
          />
          {days.map(day => {
            const dayIsToday = isToday(day)

            return (
              <Box key={day.toISODate()} className={classes.weekHeaderCell}>
                <Box className={classes.weekHeaderLabel}>
                  <span className={classes.weekDayName}>
                    {getDayLabel(day)}
                  </span>
                  <span
                    className={
                      dayIsToday
                        ? classes.weekDayNumberToday
                        : classes.weekDayNumber
                    }
                  >
                    {day.toFormat('d')}
                  </span>
                </Box>
              </Box>
            )
          })}
        </Box>
      ) : null}

      <Box className={classes.timeGridBody} ref={bodyRef}>
        <Box
          className={classes.hourLabelsColumn}
          style={{ width: labelWidth, height: gridHeight }}
        >
          {hours.map(hour => (
            <Typography
              key={hour}
              className={classes.hourLabel}
              style={{ height: hourHeight }}
            >
              {formatHourLabel(hour)}
            </Typography>
          ))}
        </Box>

        <Box className={classes.dayColumns}>
          {days.map(day => {
            const availabilitySegments = availabilityByDate?.[day.toISODate()]
            const scheduleDayBookings = bookingsByDay[day.toISODate()] || []
            const scheduleBlocked = scheduleDayBookings.filter(
              booking =>
                getScheduleEventType(booking) ===
                SCHEDULE_EVENT_TYPES.BLOCKED_TIME,
            )
            const nonBlockedBookings = scheduleDayBookings.filter(
              booking =>
                getScheduleEventType(booking) !==
                SCHEDULE_EVENT_TYPES.BLOCKED_TIME,
            )
            const blockedFromAvailability = availabilitySegments
              ? mergeBlockedSegmentsForDisplay(
                availabilitySegments.blocked || [],
              ).map(segment => blockedSegmentToBooking(segment, day))
              : []
            const blockedBookings = availabilityByDate
              ? dedupeBlockedBookings(scheduleBlocked, blockedFromAvailability)
              : scheduleBlocked
            const dayBookings = [...nonBlockedBookings, ...blockedBookings]

            return (
              <Box
                key={day.toISODate()}
                className={classes.dayColumn}
                style={{ height: gridHeight }}
              >
                {hours.map(hour =>
                  renderHourCell(day, hour, dayBookings, availabilitySegments),
                )}
                {dayBookings.map(booking => {
                  const position = getEventGridPosition(
                    booking,
                    startHour,
                    hourHeight,
                  )

                  return (
                    <CalendarEventBlock
                      key={getBookingEventKey(booking)}
                      booking={booking}
                      style={position}
                      compact={position.height < 44}
                      onReschedule={onReschedule}
                      onPayment={onPayment}
                      onCancellation={onCancellation}
                    />
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Box>
      {showAvailabilityLegend ? <ScheduleAvailabilityLegend /> : null}
    </>
  )
}

CalendarTimeGrid.propTypes = {
  days: PropTypes.arrayOf(PropTypes.object).isRequired,
  bookings: PropTypes.arrayOf(PropTypes.object).isRequired,
  startHour: PropTypes.number.isRequired,
  endHour: PropTypes.number.isRequired,
  title: PropTypes.string,
  canBookStudent: PropTypes.bool,
  bookingUrl: PropTypes.string,
  onBookSlotClick: PropTypes.func,
  onReschedule: PropTypes.func,
  onPayment: PropTypes.func,
  onCancellation: PropTypes.func,
  availabilityByDate: PropTypes.object,
  showAvailabilityLegend: PropTypes.bool,
}

export default CalendarTimeGrid
